import type { Prisma, TicketUserType } from "@prisma/client";
import type {
  BuyableItemEvent,
  BuyableItemHandler,
  PartialBuyableItem,
} from "../buyable-item.types";

type PartialTicketBuyableItem = {
  ticketType?: { id: string; name?: string } | null;
};

type TicketBuyableItem = {
  ticketType: {
    id: string;
    name?: string;
    quantityLeft?: number | null;
    groupMinSize?: number | null;
    // Lets a client cart tell Group tickets apart (checkout email lock, separate listing).
    userType?: TicketUserType | null;
    event?: BuyableItemEvent;
  };
};

type TicketWithEvent = {
  ticketType: { event: BuyableItemEvent };
};

const GROUP_KEY_PREFIX = "ticket";

export const TicketBuyableItemHandler = {
  displayName: "Billet",
  isCumulative: true,
  accounting: {
    generalAccountCode: "706119",
    invoiceLabel: "Billetterie",
  },

  getCompleteItemName(item: PartialBuyableItem): string | null {
    return item.ticketType?.name ? `Billet: ${item.ticketType.name}` : null;
  },

  getEvent(item: TicketWithEvent): BuyableItemEvent {
    return item.ticketType.event;
  },

  isValid(item: PartialTicketBuyableItem): boolean {
    return !!item.ticketType?.id;
  },

  findEquivalent(item: PartialTicketBuyableItem): Prisma.BuyableItemWhereInput {
    return { ticketType: { id: item.ticketType?.id } };
  },

  create({ ticketType }: TicketBuyableItem): Omit<Prisma.BuyableItemCreateInput, "buyableType"> {
    return { ticketType: { connect: { id: ticketType.id } } };
  },

  parseGroupKeyAsWhere(groupKey: string): Prisma.BuyableItemWhereInput {
    if (!TicketBuyableItemHandler.isGroupKey(groupKey)) {
      throw new Error(`Invalid group key for ticket: ${groupKey}`);
    }
    const [, ticketTypeId] = groupKey.split("|");
    return { ticketType: { id: ticketTypeId } };
  },

  isGroupKey(groupKey: string): boolean {
    return groupKey.startsWith(`${GROUP_KEY_PREFIX}|`);
  },

  getGroupKey(item: PartialTicketBuyableItem): string {
    const ticketTypeId = item.ticketType?.id;
    if (!ticketTypeId) {
      throw new Error("Cannot get group key for invalid ticket item");
    }
    return `${GROUP_KEY_PREFIX}|${ticketTypeId}`;
  },

  getPricingQuery(item: PartialBuyableItem): Prisma.PricingFindFirstArgs {
    return { where: { ticketType: { id: item.ticketType?.id } } };
  },

  getEventSearchFilter(searchText: string): Prisma.BuyableItemWhereInput {
    return {
      ticketType: {
        event: { name: { contains: searchText, mode: "insensitive" } },
      },
    };
  },
} as const satisfies BuyableItemHandler;
